/**
 * Story 24.4 (review loop 1) — is the router still on the entry the app
 * booted on?
 *
 * react-router gives a FRESH initial entry the location key `"default"`, but
 * `BrowserRouter` reads the key from `history.state?.key`, so a reload (or a
 * back/forward into the SPA) of an entry the app pushed restores that entry's
 * stored key. PageHeader must not steal focus on what is really a first load,
 * so this module learns navigation from `history` itself, installed once
 * when `main.tsx` imports it first — before the router or any lazy page:
 *
 * - The boot key is `history.state?.key ?? "default"` at install time.
 * - A `pushState` or a `popstate` (Back/Forward) marks the app as navigated.
 * - A `replaceState` before any navigation adopts the new state's key as the
 *   boot key, so the router's own boot write and a boot-time
 *   `<Navigate replace>` redirect stay "initial" — but only until the person
 *   has pressed a key or a pointer on this document. After that a replace is
 *   a navigation they asked for (a form on a first-loaded page that ends in
 *   `navigate(…, { replace: true })`), and it ends the first load like a
 *   push does. A boot redirect that lands after an early press is therefore
 *   treated as a navigation too, which only means its `h1` takes focus.
 *
 * `isInitialEntry(key)` is a pure read: `!navigated && key === bootKey`.
 * Once navigated, Back to the first entry is an ordinary navigation.
 *
 * The wrappers sit on `History.prototype`, not on the `history` instance:
 * `useBackDismiss` installs its own instance wrappers only over the native
 * prototype method and writes through `History.prototype.*`, so both chain
 * (its wrapper → this one → the native method) and neither displaces the
 * other.
 */

type HistoryStateWithKey = { key?: unknown } | null | undefined

function keyOf(state: unknown): string {
  const key = (state as HistoryStateWithKey)?.key
  return typeof key === "string" && key ? key : "default"
}

let bootKey = typeof window === "undefined" ? "default" : keyOf(window.history.state)
let navigated = false
// Has the person pressed a key or a pointer on this document yet?
let acted = false
let installed = false

function install(): void {
  if (installed || typeof window === "undefined" || typeof History === "undefined") return
  installed = true
  const proto = History.prototype
  const nativePush = proto.pushState
  const nativeReplace = proto.replaceState
  proto.pushState = function pushState(this: History, ...args: Parameters<History["pushState"]>) {
    navigated = true
    return nativePush.apply(this, args)
  }
  proto.replaceState = function replaceState(this: History, ...args: Parameters<History["replaceState"]>) {
    if (!navigated) {
      if (acted) navigated = true
      else bootKey = keyOf(args[0])
    }
    return nativeReplace.apply(this, args)
  }
  window.addEventListener("popstate", () => {
    navigated = true
  })
  const markActed = () => {
    acted = true
  }
  window.addEventListener("pointerdown", markActed, { capture: true, passive: true })
  window.addEventListener("keydown", markActed, { capture: true, passive: true })
}

install()

function isInitialEntry(key: string): boolean {
  return !navigated && key === bootKey
}

/** Tests only: re-capture the boot key from the current history state. */
function resetInitialEntryForTests(): void {
  bootKey = typeof window === "undefined" ? "default" : keyOf(window.history.state)
  navigated = false
  acted = false
}

export { isInitialEntry, resetInitialEntryForTests }
